import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from "@mui/material";
import {CheckCircle, Cancel} from "@mui/icons-material";
import CircularLoading from "../components/CircularLoading";
import TextInput from "../components/forms/TextInput";
import {put, SPONSORED_NOTIFICATIONS} from "../services/Client";
import {
    useGetAllSponsoredNotifications,
    useGetPendingSponsoredNotifications
} from "../services/ContentManager";

/** Etichetta e colore dello stato di moderazione. */
function statoChip(stato, t) {
    switch (stato) {
        case 'APPROVED':
            return {label: t('notificationApproved'), color: 'success'};
        case 'REJECTED':
            return {label: t('notificationRejected'), color: 'error'};
        default:
            return {label: t('notificationPending'), color: 'warning'};
    }
}

/**
 * Coda di moderazione delle notifiche sponsorizzate.
 * <p>
 * Una sponsorizzata nasce ferma: da qui la si legge prima che esca sui telefoni dei
 * pinbiker. Approvando si puo' anche concedere all'esercente l'invio senza attesa,
 * che e' la scelta che si fa una volta sola, al primo contenuto accettabile.
 */
export default function SponsoredNotificationsModeration() {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [mostraStorico, setMostraStorico] = useState(false);
    const [inEsame, setInEsame] = useState(null);
    const [azione, setAzione] = useState(null);
    const [nota, setNota] = useState('');
    const [fiducia, setFiducia] = useState(false);
    const [inCorso, setInCorso] = useState(false);

    const attesa = useGetPendingSponsoredNotifications();
    const storico = useGetAllSponsoredNotifications(mostraStorico);

    const elenco = mostraStorico ? storico.notifications : attesa.notifications;
    const caricamento = mostraStorico ? storico.status === 'loading' : attesa.status === 'loading';

    const apri = (notifica, quale) => {
        setInEsame(notifica);
        setAzione(quale);
        setNota('');
        setFiducia(false);
    };

    const chiudi = () => {
        setInEsame(null);
        setAzione(null);
    };

    const conferma = () => {
        setInCorso(true);
        const params = azione === 'approve' ? {note: nota, trustVendor: fiducia} : {note: nota};
        put(SPONSORED_NOTIFICATIONS, {elem: `moderation/${inEsame.id}/${azione}`, params})
            .then(() => {
                enqueueSnackbar(t('saved'), {variant: 'success'});
                chiudi();
                attesa.refetch();
                if (mostraStorico) storico.refetch();
            })
            .catch(() => enqueueSnackbar(t('errorSavingData'), {variant: 'error'}))
            .finally(() => setInCorso(false));
    };

    if (caricamento) return <CircularLoading/>;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4">{t('notificationModeration')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('notificationModerationExplain')}
                    </Typography>
                </Box>
                <FormControlLabel
                    control={<Switch checked={mostraStorico}
                                     onChange={(e) => setMostraStorico(e.target.checked)}/>}
                    label={t('showAll')}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('shop')}</TableCell>
                            <TableCell>{t('notificationTitle')}</TableCell>
                            <TableCell>{t('targetType')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell align="right"/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {elenco.map((n) => {
                            const chip = statoChip(n.status, t);
                            return (
                                <TableRow key={n.id} hover>
                                    <TableCell>
                                        {n.createdDate ? new Date(n.createdDate).toLocaleDateString('it-IT') : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {n.vendorBusinessName || '-'}
                                        {n.vendorAutoApproved &&
                                            <Chip size="small" sx={{ml: 1}} variant="outlined"
                                                  label={t('notificationAutoApprovedVendor')}/>}
                                    </TableCell>
                                    <TableCell sx={{maxWidth: 380}}>
                                        <Typography variant="body2" fontWeight={700}>{n.title}</Typography>
                                        <Typography variant="body2">{n.body}</Typography>
                                        {n.moderationNote &&
                                            <Typography variant="caption" color="textSecondary">
                                                {t('notificationModerationNote')}: {n.moderationNote}
                                            </Typography>}
                                    </TableCell>
                                    <TableCell>
                                        {n.targetType === 'GEO_RADIUS' ? t('geoRadius') : t('allUsers')}
                                    </TableCell>
                                    <TableCell><Chip size="small" color={chip.color} label={chip.label}/></TableCell>
                                    <TableCell align="right">
                                        {n.status !== 'APPROVED' &&
                                            <Button size="small" startIcon={<CheckCircle/>}
                                                    onClick={() => apri(n, 'approve')}>
                                                {t('notificationApprove')}
                                            </Button>}
                                        {n.status !== 'REJECTED' &&
                                            <Button size="small" color="secondary" startIcon={<Cancel/>}
                                                    onClick={() => apri(n, 'reject')}>
                                                {t('notificationReject')}
                                            </Button>}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {elenco.length === 0 &&
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="textSecondary" sx={{py: 3}}>
                                        {t('noSponsoredNotifications')}
                                    </Typography>
                                </TableCell>
                            </TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!inEsame} onClose={chiudi} fullWidth maxWidth="sm">
                <DialogTitle>
                    {azione === 'approve' ? t('notificationApprove') : t('notificationReject')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 2}}>
                        <strong>{inEsame?.title}</strong><br/>{inEsame?.body}
                    </Typography>
                    <TextInput
                        value={nota}
                        label={t('notificationModerationNote')}
                        onTextChange={setNota}
                        multiline
                        fullWidth
                    />
                    {azione === 'approve' &&
                        <FormControlLabel
                            sx={{mt: 2}}
                            control={<Switch checked={fiducia}
                                             onChange={(e) => setFiducia(e.target.checked)}/>}
                            label={t('notificationTrustVendor')}
                        />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={chiudi} color="secondary">{t('abort')}</Button>
                    <Button onClick={conferma} variant="contained" disabled={inCorso}>
                        {t('confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
